import {csrfToken, ApiError} from './api';

export type InvoiceState='issued'|'checkout_open'|'partially_paid'|'settled'|'external_payment_confirmed'|'waived'|'void'|'manual_resolution_required';
export type InvoiceSummary={invoice_id:string;invoice_number:string;state:InvoiceState;row_version:number;current_version:number;company_file_id:string;file_number:string;recipient_name:string;total_minor:number;paid_minor:number;balance_minor:number;currency:'USD';due_date:string|null;updated_at:string};
export type InvoiceLineInput={description:string;quantity:number;unit_amount_minor:number};
export type InvoiceIssueInput={recipient_name:string;recipient_email:string|null;due_date:string|null;lines:InvoiceLineInput[];service_job_ids:string[]};
export type InvoiceCommand={action:'void'|'waive';reason_code:string}|{action:'record_external_payment';amount_minor:number;evidence_reference:string};

const uuid=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const states:InvoiceState[]=['issued','checkout_open','partially_paid','settled','external_payment_confirmed','waived','void','manual_resolution_required'];
const count=(v:unknown)=>typeof v==='number'&&Number.isSafeInteger(v)&&v>=0;

function invoiceSummary(value:unknown):InvoiceSummary{
  const v=value as Record<string,unknown>|null;
  if(!v||typeof v!=='object'||Array.isArray(v)||typeof v.invoice_id!=='string'||!uuid.test(v.invoice_id)||typeof v.company_file_id!=='string'||!uuid.test(v.company_file_id)
    ||typeof v.invoice_number!=='string'||!/^[A-Z0-9][A-Z0-9-]{2,31}$/.test(v.invoice_number)||!states.includes(v.state as InvoiceState)
    ||!count(v.row_version)||Number(v.row_version)<1||!count(v.current_version)||Number(v.current_version)<1||!count(v.total_minor)||!count(v.paid_minor)||!count(v.balance_minor)
    ||v.currency!=='USD'||typeof v.recipient_name!=='string'||typeof v.file_number!=='string'||(v.due_date!==null&&typeof v.due_date!=='string')||typeof v.updated_at!=='string')throw new Error('Invoice response mismatch.');
  return {invoice_id:v.invoice_id,invoice_number:v.invoice_number,state:v.state as InvoiceState,row_version:Number(v.row_version),current_version:Number(v.current_version),company_file_id:v.company_file_id,file_number:v.file_number,recipient_name:v.recipient_name,total_minor:Number(v.total_minor),paid_minor:Number(v.paid_minor),balance_minor:Number(v.balance_minor),currency:'USD',due_date:v.due_date as string|null,updated_at:v.updated_at};
}

async function request(path:string,body?:unknown){
  const response=await fetch(path,{method:body===undefined?'GET':'POST',credentials:'same-origin',headers:{Accept:'application/json',...(body===undefined?{}:{'Content-Type':'application/json','X-ProFenz-CSRF':csrfToken()})},...(body===undefined?{}:{body:JSON.stringify(body)})});
  if(!response.ok)throw new ApiError(response.status,{message:'Invoice change could not be confirmed.'});
  return response.json() as Promise<Record<string,unknown>>;
}

/** Validates the receipt and that it is for the exact requested invoice/file before reporting success. */
function receipt(body:Record<string,unknown>,expect:{invoice_id?:string;company_file_id?:string}):{invoice:InvoiceSummary;created:boolean}{
  if(typeof body.created!=='boolean')throw new Error('Invoice receipt mismatch.');
  const invoice=invoiceSummary(body.invoice);
  if((expect.invoice_id&&invoice.invoice_id!==expect.invoice_id)||(expect.company_file_id&&invoice.company_file_id!==expect.company_file_id))throw new Error('Invoice receipt mismatch.');
  return {invoice,created:body.created};
}

export async function loadCompanyInvoices(fileId:string):Promise<InvoiceSummary[]>{
  if(!uuid.test(fileId))throw new Error('Invalid file.');
  const body=await request(`/api/v1/company/operations/files/${fileId}/invoices`);
  if(!Array.isArray(body.invoices))throw new Error('Invoice response mismatch.');
  return body.invoices.map(invoiceSummary);
}

export async function issueCompanyInvoice(fileId:string,input:InvoiceIssueInput,key:string){
  if(!uuid.test(fileId))throw new Error('Invalid file.');
  return receipt(await request(`/api/v1/company/operations/files/${fileId}/invoices`,{...input,idempotency_key:key}),{company_file_id:fileId});
}

export async function changeCompanyInvoice(invoice:InvoiceSummary,command:InvoiceCommand,key:string){
  if(!uuid.test(invoice.invoice_id))throw new Error('Invalid invoice.');
  const result=receipt(await request(`/api/v1/company/operations/invoices/${invoice.invoice_id}/actions`,{...command,expected_row_version:invoice.row_version,idempotency_key:key}),{invoice_id:invoice.invoice_id,company_file_id:invoice.company_file_id});
  if(result.invoice.row_version<=invoice.row_version)throw new Error('Invoice receipt mismatch.');
  return result;
}

/** Parses a non-negative US dollar amount ("125", "125.5", "125.50") into cents, or null. */
export function dollarsToMinor(value:string):number|null{
  const match=/^(\d{1,6})(?:\.(\d{1,2}))?$/.exec(value.trim());
  if(!match)return null;
  return Number(match[1])*100+Number((match[2]??'').padEnd(2,'0'));
}

export function formatMinor(minor:number):string{
  return new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(minor/100);
}
